/**
 * Moderator actions, on the wire.
 *
 * These used to be local mocks: `applyParticipantOverride` flipped the host's
 * copy of a participant so the panel re-rendered, and nothing reached the
 * target — the host saw a muted tile while the person kept talking. Every
 * action now goes through the room channel, and every target honours what it
 * receives.
 *
 * Semantics are soft: a stopped participant may turn their own media back on.
 * Nothing here revokes that, which is why "Request audio/video" exists as the
 * counterpart. Making it hard would need per-participant permission state
 * enforced on the SFU, not just a client that agrees to stay muted.
 *
 * Authorization is the server's: the channel rejects `moderate:*` from anyone
 * who is not a live moderator, so hiding these controls is presentation, not
 * protection.
 */

import { useCallback, useEffect } from 'react'
import type { PhoenixEventHandler } from './usePhoenixChannel'
import { vclog } from './vc-log'
import type { PresenterRequestSubject } from '@vc/views/dialogs'
import { isShareOnlyPeer } from '../services/share-only'
import { readActor, type Actor } from '../services/moderator-notices'
import { useAnnotationRequestsStore } from '../store/annotation-requests.store'

/**
 * Who is really asking, besides the name they go by: the account's email, and
 * whether the asker is a screen-share session (contract §4) — whose name is
 * whatever was typed on its page. Both absent from an older server.
 */
export type PresenterRequestDetails = { email: string | null; shareOnly: boolean }

/**
 * Every handler below that a moderator's command causes is handed who sent it
 * (`by`, `byName` — null from a server that does not say), so the notice can
 * name the co-host instead of "the host". `bulk` is a command sent with no
 * target: Mute all, Stop all videos, Stop all screens.
 */
export type ModerationHandlers = {
  /** Turn the local mic off because a moderator asked. */
  onForceMute: (by: Actor | null, bulk: boolean) => void
  /** Turn the local camera off because a moderator asked. */
  onForceStopVideo: (by: Actor | null, bulk: boolean) => void
  /** Stop the local screen share because a moderator asked. */
  onForceStopScreenShare: (by: Actor | null, bulk: boolean) => void
  /** The meeting was ended for everyone. */
  onMeetingEnded: (by: Actor | null) => void
  /** A moderator removed this participant — leave, do not just hide anyone. */
  onRemoved: (by: Actor | null) => void
  /**
   * A moderator removed somebody else. Optional: only the notice to the other
   * moderators needs it; the roster drops them on its own.
   */
  onParticipantRemoved?: (peerId: string, by: Actor | null) => void
  /**
   * A moderator asked this participant to turn their mic/camera on. An ask,
   * not a command — prompt them, never enable it for them.
   */
  onMediaRequested: (kind: 'audio' | 'video', by: Actor | null) => void
  /**
   * Someone asked to present. Delivered to EVERY peer — the channel cannot
   * address moderators only — so the handler is responsible for ignoring it
   * unless this client can actually answer.
   */
  onPresenterRequested: (
    peerId: string,
    name: string,
    subject: PresenterRequestSubject,
    details: PresenterRequestDetails,
  ) => void
  /** What an ask was for, learned out-of-band; may arrive before or after it. */
  onPresenterSubject: (peerId: string, subject: PresenterRequestSubject) => void
  /** A moderator refused this participant's request to present. */
  onPresenterDenied: (by: Actor | null) => void
  /**
   * SOMEONE's request was refused — target included, whoever it was.
   *
   * Separate from `onPresenterDenied` because moderators need it too: a room
   * can have several, they all queued the same ask, and without this the ones
   * who did not answer keep showing a dialog for a question already settled.
   */
  onPresenterAnswered: (peerId: string) => void
}

/** The room channel's answer to a command (`status` is ok / error / timeout / dropped). */
export type ChannelReply = { status: string; response: Record<string, unknown> }

export type Moderation = {
  muteParticipant: (peerId: string) => void
  stopParticipantVideo: (peerId: string) => void
  /**
   * Resolves with the channel's answer. `ok` means it was relayed, not that the
   * share has ended: the presenter's browser ends it, and the share leaving the
   * room is the confirmation (see services/share-stop-requests).
   */
  stopParticipantScreenShare: (peerId: string) => Promise<ChannelReply>
  muteAll: () => void
  stopAllVideos: () => void
  stopAllScreenShares: () => void
  setRole: (peerId: string, role: 'host' | 'cohost' | 'attendee') => void
  setPresenter: (peerId: string, granted: boolean) => void
  /** Ask a moderator for presenter. The only ask that travels upward. */
  requestPresenter: (subject?: PresenterRequestSubject) => void
  /** Withdraw my request to present before anyone answered it. */
  cancelPresenterRequest: () => void
  /**
   * Refuse a request. There is no matching "approve" — approving IS
   * `setPresenter(id, true)`, which grants and rebroadcasts roles; a refusal
   * changes nothing and exists only to answer the asker.
   */
  denyPresenter: (peerId: string) => void
  requestAudio: (peerId: string) => void
  requestVideo: (peerId: string) => void
  removeParticipant: (peerId: string) => void
  /**
   * Let a screen-share page a moderator removed join this meeting again
   * (decision 11). Only that: it asks to present again like any other page.
   */
  allowShare: (peerId: string) => void
  /** Ask a moderator for annotation access, from the pen over a document. */
  requestAnnotation: () => void
  /** Grant or revoke annotation access. Approving a request IS a grant. */
  setAnnotator: (peerId: string, granted: boolean) => void
  /** Refuse an annotation request; it only answers the asker. */
  denyAnnotator: (peerId: string) => void
  endMeetingForAll: () => void
}

type Args = {
  peerId: string | null
  sendEvent: (event: string, payload: Record<string, unknown>) => void
  /** For the commands whose answer the UI shows — see stopParticipantScreenShare. */
  request: (event: string, payload: Record<string, unknown>) => Promise<ChannelReply>
  subscribe: (event: string, handler: PhoenixEventHandler) => () => void
  handlers: ModerationHandlers
}

export function useModeration({ peerId, sendEvent, request, subscribe, handlers }: Args): Moderation {
  const {
    onForceMute,
    onForceStopVideo,
    onForceStopScreenShare,
    onMeetingEnded,
    onMediaRequested,
    onRemoved,
    onParticipantRemoved,
    onPresenterRequested,
    onPresenterSubject,
    onPresenterDenied,
    onPresenterAnswered,
  } = handlers

  // Receiving side. A bulk action arrives with no targetPeerId — the server
  // broadcasts from the sender, so everyone who receives it is a target.
  useEffect(() => {
    const forMe = (payload: Record<string, unknown>) => {
      const target = payload.targetPeerId
      return target == null || target === peerId
    }
    const bulk = (payload: Record<string, unknown>) => payload.targetPeerId == null

    const offs = [
      subscribe('moderate:mute', (payload) => {
        if (!forMe(payload)) return
        vclog('presence', 'force mute', { by: payload.by })
        onForceMute(readActor(payload), bulk(payload))
      }),
      subscribe('moderate:stop_video', (payload) => {
        if (!forMe(payload)) return
        vclog('presence', 'force stop video', { by: payload.by })
        onForceStopVideo(readActor(payload), bulk(payload))
      }),
      subscribe('moderate:stop_screenshare', (payload) => {
        if (!forMe(payload)) return
        vclog('presence', 'force stop screenshare', { by: payload.by })
        onForceStopScreenShare(readActor(payload), bulk(payload))
      }),
      subscribe('meeting:ended', (payload) => {
        vclog('presence', 'meeting ended', { by: payload?.by })
        onMeetingEnded(readActor(payload))
      }),
      subscribe('moderate:request_audio', (payload) => {
        if (!forMe(payload)) return
        vclog('presence', 'audio requested', { by: payload.by })
        onMediaRequested('audio', readActor(payload))
      }),
      subscribe('moderate:request_video', (payload) => {
        if (!forMe(payload)) return
        vclog('presence', 'video requested', { by: payload.by })
        onMediaRequested('video', readActor(payload))
      }),
      subscribe('presenter:request', (payload) => {
        const by = typeof payload.by === 'string' ? payload.by : null
        if (!by || by === peerId) return
        vclog('presence', 'presenter requested', { by })
        // Older signaling omits `subject`; screen share is what it always
        // meant, so that is the fallback rather than an unknown state.
        const subject =
          payload.subject === 'document' ||
          payload.subject === 'video' ||
          payload.subject === 'whiteboard'
            ? payload.subject
            : 'screenShare'
        onPresenterRequested(by, typeof payload.name === 'string' ? payload.name : '', subject, {
          email: typeof payload.email === 'string' && payload.email ? payload.email : null,
          shareOnly: isShareOnlyPeer(by, { share_only: payload.share_only }),
        })
      }),
      // The out-of-band half of the pair above. `broadcast` echoes to the
      // sender too (unlike `broadcast_from`), so skip our own.
      subscribe('broadcast', (payload) => {
        const wire = (payload ?? {}) as { from?: string; body?: unknown }
        const body = wire.body
        if (!body || typeof body !== 'object') return
        const env = body as Record<string, unknown>
        if (env.__inmeet !== 'presenter') return
        const from = typeof wire.from === 'string' ? wire.from : null
        if (!from || from === peerId) return
        // The asker took it back: it leaves every moderator's queue, the same
        // way an answer from another moderator does.
        if (env.action === 'cancel') {
          onPresenterAnswered(from)
          return
        }
        if (env.action !== 'request') return
        if (
          env.subject !== 'document' &&
          env.subject !== 'video' &&
          env.subject !== 'whiteboard' &&
          env.subject !== 'screenShare'
        )
          return
        onPresenterSubject(from, env.subject)
      }),
      subscribe('moderate:deny_presenter', (payload) => {
        const target = payload.targetPeerId
        // Everyone dequeues; only the target is told.
        if (typeof target === 'string') onPresenterAnswered(target)
        if (!forMe(payload)) return
        vclog('presence', 'presenter request denied', { by: payload.by })
        onPresenterDenied(readActor(payload))
      }),
      // Annotation asks. Straight into their store rather than through a
      // handler: nothing else reacts to them, and MeetingScreen shows the queue
      // to moderators only, in PresenterRequestDialog.
      subscribe('annotation:request', (payload) => {
        const by = typeof payload.by === 'string' ? payload.by : null
        if (!by || by === peerId) return
        vclog('presence', 'annotation requested', { by })
        useAnnotationRequestsStore
          .getState()
          .add({ peerId: by, name: typeof payload.name === 'string' ? payload.name : '' })
      }),
      subscribe('moderate:deny_annotator', (payload) => {
        const target = payload.targetPeerId
        if (typeof target !== 'string') return
        // Everyone dequeues; only the asker is told.
        const requests = useAnnotationRequestsStore.getState()
        requests.remove(target)
        if (target === peerId) {
          vclog('presence', 'annotation request denied', { by: payload.by })
          requests.setMyOutcome('rejected', readActor(payload))
        }
      }),
      // Unlike the `request_*` asks, this one is not declinable.
      //
      // Deliberately not `forMe`: that treats a missing targetPeerId as a bulk
      // action for everyone who receives it, which is right for mute-all and
      // stop-all-videos because those exist. There is no remove-all — nothing
      // in either client sends a targetless removal — so an absent target here
      // is a malformed payload, and answering it by ejecting the entire room
      // is the worst available reading.
      subscribe('moderate:remove', (payload) => {
        const target = payload.targetPeerId
        if (typeof target !== 'string' || !target) return
        if (target !== peerId) {
          onParticipantRemoved?.(target, readActor(payload))
          return
        }
        vclog('presence', 'removed from meeting', { by: payload.by })
        onRemoved(readActor(payload))
      }),
    ]

    return () => offs.forEach((off) => off())
  }, [
    subscribe,
    peerId,
    onForceMute,
    onForceStopVideo,
    onForceStopScreenShare,
    onMeetingEnded,
    onMediaRequested,
    onRemoved,
    onParticipantRemoved,
    onPresenterRequested,
    onPresenterSubject,
    onPresenterDenied,
    onPresenterAnswered,
  ])

  const send = useCallback(
    (event: string, payload: Record<string, unknown> = {}) => sendEvent(event, payload),
    [sendEvent],
  )

  return {
    muteParticipant: useCallback((id) => send('moderate:mute', { targetPeerId: id }), [send]),
    stopParticipantVideo: useCallback(
      (id) => send('moderate:stop_video', { targetPeerId: id }),
      [send],
    ),
    // A request, not a send: a refusal (`host_protected`) or a frame that never
    // left had nothing to show for it, and the host clicked again.
    stopParticipantScreenShare: useCallback(
      (id) => request('moderate:stop_screenshare', { targetPeerId: id }),
      [request],
    ),
    // No targetPeerId → everyone except the sender, so a host muting all does
    // not mute themselves.
    muteAll: useCallback(() => send('moderate:mute'), [send]),
    stopAllVideos: useCallback(() => send('moderate:stop_video'), [send]),
    stopAllScreenShares: useCallback(() => send('moderate:stop_screenshare'), [send]),
    setRole: useCallback(
      (id, role) => send('moderate:set_role', { targetPeerId: id, role }),
      [send],
    ),
    setPresenter: useCallback(
      (id, granted) => send('moderate:set_presenter', { targetPeerId: id, granted }),
      [send],
    ),
    requestPresenter: useCallback(
      (subject: PresenterRequestSubject = 'screenShare') => {
        send('presenter:request', { subject })
        // Say it twice, on purpose.
        //
        // `presenter:request` only carries `subject` on a server that forwards
        // it; every build before that discards the payload and rebroadcasts
        // `by` + `name` alone, so moderators fall back to "screen share" for a
        // document ask. `broadcast` is the one relay EVERY build has — the same
        // reason shared media rides it — so this reaches the room whether or
        // not signaling has been deployed. Both routes are idempotent: they set
        // the same field to the same value.
        send('broadcast', { body: { __inmeet: 'presenter', action: 'request', subject } })
      },
      [send],
    ),
    cancelPresenterRequest: useCallback(
      () => send('broadcast', { body: { __inmeet: 'presenter', action: 'cancel' } }),
      [send],
    ),
    denyPresenter: useCallback((id) => send('moderate:deny_presenter', { targetPeerId: id }), [send]),
    requestAudio: useCallback(
      (id) => send('moderate:request_audio', { targetPeerId: id }),
      [send],
    ),
    requestVideo: useCallback(
      (id) => send('moderate:request_video', { targetPeerId: id }),
      [send],
    ),
    removeParticipant: useCallback(
      (id) => send('moderate:remove', { targetPeerId: id }),
      [send],
    ),
    allowShare: useCallback((id) => send('moderate:allow_share', { targetPeerId: id }), [send]),
    requestAnnotation: useCallback(() => send('annotation:request'), [send]),
    setAnnotator: useCallback(
      (id, granted) => send('moderate:set_annotator', { targetPeerId: id, granted }),
      [send],
    ),
    denyAnnotator: useCallback((id) => send('moderate:deny_annotator', { targetPeerId: id }), [send]),
    endMeetingForAll: useCallback(() => send('moderate:end_meeting'), [send]),
  }
}
